// SystemHealthCenter — مركز "صحة النظام" للأدمن (مقترح 3.2.0).
// لوحة قراءة-فقط تُظهر جاهزية النظام التشغيلية في بطاقات: قاعدة البيانات،
// البريد، الإصدارات، التخزين، النسخ الاحتياطية، ومعلومات العملية.
// موحّدة على مجموعة مكوّنات SmartPage + ثيم SMART_COLORS (3.4.0 / N13).
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

// ثوانٍ → نص مقروء (مثل "2h 15m" أو "45s").
function formatUptime(seconds) {
  const s = Number(seconds) || 0;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (!parts.length) parts.push(`${Math.floor(s)}s`);
  return parts.join(' ');
}

function StatusChip({ ok, okLabel, badLabel }) {
  return (
    <Chip size="small" label={ok ? okLabel : badLabel} sx={{ fontWeight: 700, color: '#fff', bgcolor: ok ? C.green : C.rose }} />
  );
}

function HealthCard({ title, status, children }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderColor: C.line, bgcolor: C.card }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 900, color: C.text }}>{title}</Typography>
          {status}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
      <Typography variant="body2" sx={{ color: C.muted }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: C.text }}>{value ?? '—'}</Typography>
    </Stack>
  );
}

export default function SystemHealthCenter() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/system-health');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fe = data?.versions?.frontend || {};

  return (
    <SmartPage>
      <SmartHeader
        title={t('systemHealth.title', 'صحة النظام')}
        actions={
          <Button onClick={load} startIcon={<RefreshIcon />} variant="contained"
            sx={{ bgcolor: C.gold, '&:hover': { bgcolor: C.goldHover } }}>
            {t('refresh', 'تحديث')}
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: C.gold }} /></Box>
      ) : data ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <HealthCard
                title={t('systemHealth.database', 'قاعدة البيانات')}
                status={<StatusChip ok={!!data.database?.ok} okLabel={t('systemHealth.connected', 'متّصل')} badLabel={t('systemHealth.down', 'متعطّل')} />}
              >
                <Row label={t('systemHealth.latency', 'زمن الاستجابة')} value={`${data.database?.latencyMs ?? '—'} ms`} />
                {data.database?.error && <Row label="error" value={data.database.error} />}
              </HealthCard>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <HealthCard
                title={t('systemHealth.mailer', 'البريد (SMTP)')}
                status={
                  !data.mailer?.enabled
                    ? <Chip size="small" label={t('systemHealth.disabled', 'معطّل')} sx={{ fontWeight: 700 }} />
                    : <StatusChip ok={!!data.mailer?.ok} okLabel={t('systemHealth.connected', 'متّصل')} badLabel={t('systemHealth.unavailable', 'غير متاح')} />
                }
              >
                <Row label={t('systemHealth.enabled', 'مفعّل')} value={data.mailer?.enabled ? '✓' : '✗'} />
              </HealthCard>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <HealthCard title={t('systemHealth.versions', 'الإصدارات')} status={null}>
                <Row label="API" value={data.versions?.backend} />
                <Row label={t('systemHealth.frontend', 'الواجهة')} value={fe.version} />
                <Row label="Node" value={data.versions?.node} />
                <Row label={t('systemHealth.environment', 'البيئة')} value={data.versions?.environment} />
              </HealthCard>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <HealthCard title={t('systemHealth.storage', 'التخزين (الرفع)')} status={null}>
                <Row label={t('systemHealth.files', 'عدد الملفات')} value={data.storage?.uploads?.files} />
                <Row label={t('systemHealth.size', 'الحجم')} value={`${data.storage?.uploads?.megabytes ?? 0} MB`} />
              </HealthCard>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <HealthCard title={t('systemHealth.backups', 'النسخ الاحتياطية')} status={null}>
                <Row label={t('systemHealth.mode', 'النمط')} value={data.backups?.mode} />
                <Row label={t('systemHealth.stored', 'مخزّنة على الخادم')} value={data.backups?.stored ? '✓' : '✗'} />
              </HealthCard>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <HealthCard title={t('systemHealth.process', 'العملية')} status={null}>
                <Row label={t('systemHealth.uptime', 'مدّة التشغيل')} value={formatUptime(data.process?.uptimeSeconds)} />
                <Row label={t('systemHealth.memory', 'الذاكرة (RSS)')} value={`${data.process?.memory?.rssMb ?? '—'} MB`} />
                <Row label={t('systemHealth.platform', 'النظام')} value={data.process?.platform} />
              </HealthCard>
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: C.muted }}>
            {t('systemHealth.generatedAt', 'تاريخ اللقطة')}: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}
          </Typography>
        </>
      ) : null}
    </SmartPage>
  );
}
